import { useState, useEffect, useRef, Suspense, useMemo } from "react";
import { useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import { Container, Box, useMediaQuery, useTheme } from "@mui/material";
import HeaderLayout from "@/layouts/full/header/Header";
import {
  WorkspaceSidebar,
  WORKSPACE_ACCENT_COLORS,
  SIDEBAR_WIDTH,
} from "@/layouts/full/sidebar";
import { _dashboardLinks, _adminDashboardLinks } from "@/utils/sidebar";
import { getCookie, setCookie } from "@/utils";
import { useAuth } from "@/hooks/auth.hooks";
import { dispatch, useSelector } from "@/redux/store";
import { getProductConfigForSubdomain, getSidebarManagement, productListMapping } from "@/utils/helpers";
import { handlingRoles } from "@/redux/apps/users/UserSlice";
import { Loader } from "@/apps/core/common";
import { Helmet } from "react-helmet-async";
import { useThemeContext } from "@/context/ThemeContext";
import { MainWrapper, PageWrapper } from "@/sections";
import { fetchLoggedUser, getSubdomain, applicationTitle } from "@/utils/common";
import { COMMON_SUB_DOMAIN, MASTER_SUB_DOMAIN, PARTNER_SUB_DOMAIN } from "@/config";
import subDomainClientMapping from "@/config/domainMapping";
import GroupSVG from "@/images/svg/group.svg";

const FullLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const subDomain = getSubdomain();
  const title = applicationTitle();
  const { fetchLoggedTokenInfo, loggedUser, handleAccountSettings } = useAuth();
  const { loggedUserOrganisations, userRoles } = useSelector((state) => state.user);
  const defaultSubDomain = subDomain !== "" && subDomain !== COMMON_SUB_DOMAIN ? subDomain : "xsm";
  const { domain: userDomain } = subDomainClientMapping(subDomain);
  //theme
  const { theme: customTheme, toggleTheme } = useThemeContext();
  //cookie
  const domainName = getCookie(`xsm_user_domain`);
  const xsmAccessToken = localStorage.getItem(`${defaultSubDomain}_access_token`);
  const selectedOrganization = getCookie(`xsm_user_organization`);
  const isAdminAccess = getCookie(`xsm_admin_access`) ?? "";

  const [showOrgDropdown, setShowOrgDropdown] = useState<boolean>(false);
  const [showProductDropdown, setShowProductDropdown] = useState<boolean>(false);
  const [listOfProducts, setListOfProducts] = useState<any>([]);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  const accessRole: any = getSidebarManagement(userRoles);
  const sidebarNavigation = useMemo(() => {
    const sidebarRoutes = _dashboardLinks.filter(
      (link: any) => accessRole[link?.title] === true
    );
    return subDomain === MASTER_SUB_DOMAIN
      ? _adminDashboardLinks
      : subDomain === PARTNER_SUB_DOMAIN
        ? _dashboardLinks
        : sidebarRoutes;
  }, [subDomain, userRoles]);

  const selectedItem = useMemo(() => {
    const links = sidebarNavigation
      .map((link) => link.url)
      .filter(Boolean) as string[];
    const currentPath = location.pathname;
    const sortedLinks = [...links].sort((a, b) => b.length - a.length);
    return sortedLinks.find(
      (link) => currentPath === link || currentPath.startsWith(`${link}/`)
    );
  }, [location.pathname, sidebarNavigation]);

  async function fetchUserInfo() {
    await fetchLoggedTokenInfo(
      xsmAccessToken ?? "",
      (domainName ?? userDomain) as string,
      isAdminAccess
    );
  }

  useEffect(() => {
    fetchUserInfo();
  }, []);

  useEffect(() => {
    const products = productListMapping(loggedUserOrganisations);
    setListOfProducts(products);
  }, [loggedUserOrganisations]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProductDropdown(false);
      }
    };

    if (showProductDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showProductDropdown]);

  const handleSelect = (selected: string) => {
    if (selected) {
      navigate(selected);
    }
  };

  // const matches1000 = useMediaQuery("(min-width: 600px) and (max-width: 1000px)");
  // const matches1200 = useMediaQuery("(min-width: 1001px) and (max-width: 1200px)");
  // const matches1400 = useMediaQuery("(min-width: 1201px) and (max-width: 1400px)");
  // const matches1500 = useMediaQuery("(min-width: 1401px)");
  // const containerPadding = matches1000
  //   ? "24px"
  //   : matches1200
  //   ? "32px"
  //   : matches1400 || matches1500
  //   ? "40px"
  //   : "16px";
  // const pageWrapperPaddingBottom = matches1000 ? "50px" : "0";

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const organizationName = !isAdminAccess
    ? selectedOrganization
      ? JSON.parse(selectedOrganization)?.name
      : "Select Organization"
    : "";

  const changeSelectedOrg = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowOrgDropdown(!showOrgDropdown);
  };

  const changeTheProducts = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (listOfProducts && listOfProducts?.length > 0) {
      setShowProductDropdown(!showProductDropdown);
    } else {
      navigate("/dashboard");
    }
  };

  const handleOrganizationSelect = async (selected: any) => {
    if (selected) {
      //save into cookie and redirect to dashboard
      await dispatch(
        handlingRoles({
          roles: loggedUserOrganisations
        })
      );
      setCookie("xsm_user_organization", JSON.stringify(selected));
      setShowOrgDropdown(false);
      navigate(0);
    }
  };

  const handleProductSelect = (item: any) => {
    window.location.href = item?.link;
    setShowProductDropdown(false);
  };

  const isAuthenticatedFullLayout = () => {
    const subDomain = getSubdomain();
    const defaultSubDomain =
      subDomain !== "" && subDomain !== COMMON_SUB_DOMAIN ? subDomain : "xsm";
    const accessTokenKey = `${defaultSubDomain}_access_token`;
    // Check for subdomain-specific token first, then fallback to generic token
    const accessToken =
      localStorage.getItem(accessTokenKey) || localStorage.getItem("xsm_access_token");
    return !!accessToken;
  };

  const productConfig = getProductConfigForSubdomain(subDomain);
  const Icon = productConfig?.icon;
  const productDisplayName = productConfig?.displayName ?? title;
  const workspaceAccentColor = productConfig?.accentColor ?? WORKSPACE_ACCENT_COLORS.partner;

  const userDisplayName = useMemo(() => {
    if (loggedUser?.first_name) {
      return `${loggedUser.first_name}${loggedUser.last_name ? ` ${loggedUser.last_name}` : ""}`;
    }
    return fetchLoggedUser() || "User";
  }, [loggedUser]);

  const userRoleLabel = isAdminAccess ? "Global Admin" : "User";

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <MainWrapper>
        {isAuthenticatedFullLayout() ? (
          <>
            <div ref={productDropdownRef}>
              <WorkspaceSidebar
                navItems={sidebarNavigation}
                selectedItem={selectedItem}
                onSelect={handleSelect}
                workspaceName={productDisplayName}
                workspaceIconSrc={Icon}
                workspaceAccentColor={workspaceAccentColor}
                organizationName={organizationName || undefined}
                onOrganizationClick={changeSelectedOrg}
                showProductSwitcher={listOfProducts?.length > 0}
                groupIcon={
                  <img src={GroupSVG} alt="workspace switcher" height={20} width={20} />
                }
                isProductDropdownOpen={showProductDropdown}
                productItems={listOfProducts}
                onProductSwitcherClick={changeTheProducts}
                onProductSelect={handleProductSelect}
                userName={userDisplayName}
                userRole={userRoleLabel}
                onSettingsClick={handleAccountSettings}
              />
            </div>
            <PageWrapper
              className="page-wrapper"
              style={{
                marginLeft: `${SIDEBAR_WIDTH}px`,
                width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
              }}
            >
              <HeaderLayout
                isSmallScreen={isSmallScreen}
                toggleTheme={toggleTheme}
                customTheme={customTheme}
                isDropdownOpen={showOrgDropdown}
                dropdownItems={loggedUserOrganisations}
                handleDropdownSelect={handleOrganizationSelect}
                changeSelectedOrg={changeSelectedOrg}
                organizationName={organizationName}
              />
              <Container
                maxWidth={false}
                sx={{
                  padding: "34px 32px !important",
                  marginTop: "71px",
                  maxWidth: "100%",
                  width: "100%",
                }}
                className="main-full-container"
              >
                <Box
                  className="outlet-box"
                  sx={{
                    minHeight: "calc(100vh - 170px)",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                  }}
                >
                  <Suspense fallback={<Loader />}>
                    <Outlet />
                  </Suspense>
                </Box>
              </Container>
            </PageWrapper>
          </>
        ) : (
          <Navigate to="/" replace />
        )}
      </MainWrapper>
    </>
  );
};

export default FullLayout;
